(ns pfo.coltype.model
  (:require
   [clojure.set]
   [clojure.string :as str]
   [taoensso.timbre :as timbre
    :refer [log trace debug info warn error fatal report logf
            tracef debugf infof warnf errorf fatalf reportf spy]]
   [sci.core :as sci]
   [pfo.controller :as controller]
   [pfo.util :as util]
   [pfo.auth.model :as auth-model]
   [pfo.api :as api]
   [promesa.core :as p]))

(declare is-visible-by-dependency get-attributes get-value)

(defn collection-type-name [ct]
  (str (get-in ct [:asset_type :name]) ": " (:name ct)))

(defn to-set [val] (if (coll? val) (set val) #{val}))

(def enable-visibility-logging false)

(defn get-attr-ref [field attr] (str (:ref field) "_" (:ref attr)))

(defn is-visible
  ([{:keys [visibility]
     :as field} get-value-for-ref mode]
   (let [debug (fn [msg val] (if enable-visibility-logging (spy :debug (str (util/bracketed (:ref field)) " " msg) val) val))]
     (debug "Visible"
       (and
         (debug "Is not nil" field)
         (debug "Not a static field" (not= "static" (:type field)))
         (debug "Visibility not none" (not= "none" (:mode visibility)))
         (debug "not hide because of lack of value"
           (not
             (and
               (debug "hide if no value" (:hide_if_no_value visibility))
               (debug "has no value" (not (get-value-for-ref (:ref field)))))))
         (debug "show because visibility mode matches"
           (or
             (debug "empty visibility mode" (empty? (:mode visibility)))
             (debug "visibilit mode any" (= "any" (:mode visibility)))
             (debug (str "visibility mode " mode) (= (:mode visibility) (name mode)))))
         (debug "either visibility ref empty or dependency condition met"
           (is-visible-by-dependency field get-value-for-ref))))))
  ([field get-value-for-ref] (is-visible field get-value-for-ref :edit)))

(defn is-visible-by-dependency [{:keys [visibility]} get-value-for-ref]
  (let [visibility-ref (:ref visibility)]
    (or
     (not (not-empty visibility-ref))
     (if (:dependency_value_present visibility)
       ((if (:inverse visibility) not identity) (util/pytrue? (get-value-for-ref visibility-ref)))
       ((if (:inverse visibility) not identity)
        (boolean (seq (clojure.set/intersection
                       (set (:values visibility))
                       (to-set (get-value-for-ref visibility-ref))))))))))

(defn collection-types [group-id order]
  (api/ajaxp "/collection_types/" {:params (util/remove-nil-values {:group_id group-id :order order})}))

(defn collection-type-form-def [collection-type-id]
  (api/ajaxp (str "/collection_types/" collection-type-id "/formdef/") {}))

(defn flatten-fields
  ([fields include-composite]
   (loop [fields fields
          result []]
     (let [field (first fields)]
       (if (seq fields)
         (if (:is_composite field)
           (recur
            (concat (:subfields field) (rest fields))
            (if include-composite
              (conj result field)
              result))
           (recur
            (rest fields)
            (conj result field)))
         result))))
  ([fields] (flatten-fields fields false)))

;; ;; From field-list, return seq of those needing objects to display properly in form
;; (defn get-fields-needing-objects [field-list ref-set]
;;   (filter
;;     (fn [fld]
;;       (and
;;         (not-empty (:loader_ref fld))
;;         (contains? ref-set (keyword (:ref fld)))))
;;     field-list))

(defn map-field [func field]
  (if (seq (:subfields field))
    (assoc (func field) :subfields (map (fn [subfield] (map-field func subfield)) (:subfields field)))
    (func field)))

(defn object-compare-key [[_ {{collection-name :name} :owner_collection
                              :as obj}]]
  (or collection-name (auth-model/get-full-name obj true)))

(defn- collection-data [collection ref]
  (let [ref (if (keyword? ref) ref (keyword ref))]
    (get-in collection [:data ref])))

(defn field-collection [field collection]
  (if-let [col-fn (:collection-fn field)] (col-fn) collection))

(defn visible-attrs 
  ([field get-ref-value-fn lonely-attr-set]
   (filter
     (fn [attr]
       (and
         (not (contains? lonely-attr-set (:ref attr)))
         (is-visible attr (fn [ref]
                            (get-ref-value-fn ref)))))
     (:attributes field)))
  ([field get-ref-value-fn] (visible-attrs field get-ref-value-fn nil)))

(defn visible-and-not-lonely-attrs [field collection get-ref-value-fn]
  (let [attrs-to-hide-if-lonely (map identity (filter :hide_lonely_attribute (:attributes field)))
        lonely-attrs (set 
                       (keys
                         (filter second
                           (update-vals
                             (group-by first (for [attr attrs-to-hide-if-lonely
                                                   row-vals (vals (get-attributes field collection))]
                                               [(:ref attr) (row-vals (keyword (:ref attr)))]))
                             (fn [attr-vals]
                               (every? (comp nil? second) attr-vals))))))]
    (visible-attrs field get-ref-value-fn lonely-attrs)))

(def custom-fields #{:ownership :collection})
(defn- field-type-dispatch [{type :type}] (clojure.set/intersection custom-fields #{(keyword type)}))

(defmulti get-value field-type-dispatch)
(defmulti get-form-value field-type-dispatch)
(defmulti get-objects field-type-dispatch)
(defmulti get-percentages field-type-dispatch)
(defmulti has-percentage field-type-dispatch)
(defmulti get-attributes field-type-dispatch)
(defmulti get-attributes-definition field-type-dispatch)
(defmulti field-column-padding (fn [_ {type :type}] (clojure.set/intersection custom-fields #{(keyword type)})))
(defmulti unpack field-type-dispatch)

(defn- generic-get-value [field collection] (get-in collection [:data (keyword (:ref field))]))
(defn- generic-get-form-value [field value-atoms] (some-> (:ref field) (value-atoms) deref))
(defn- generic-get-objects [field collection] (get-in collection [:data (keyword (str (:ref field) "_objects"))]))
(defn- generic-get-percentages [field collection] (get-in collection [:data (keyword (str (:ref field) "_percentage"))]))
(defn- generic-has-percentage [field _ _] (str/includes? (:type field) "percentage"))
(defn- generic-get-attributes [field collection] (get-in collection [:data (keyword (str (:ref field) "_attrs"))]))
(defn- generic-get-attributes-definition
  ([field ref-field-map collection mode]
   (if (= :details mode)
     (visible-and-not-lonely-attrs field collection (fn [ref] (get-value (ref-field-map ref) collection)))
     (visible-attrs field (fn [ref] (get-value (ref-field-map ref) collection)))))
  ([field ref-field-map collection] (generic-get-attributes-definition field ref-field-map collection :edit)))
(defn- generic-field-column-padding [options field]
  (-
   (:max-attribute-column-count options)
   ((:attribute-column-counts options) (:ref field))))
(defn- generic-unpack [field] [field])

(defmethod get-value #{} [field collection] (generic-get-value field collection))
(defmethod get-form-value #{} [field value-atoms] (generic-get-form-value field value-atoms))
(defmethod get-objects #{} [field collection] (generic-get-objects field collection))
(defmethod get-percentages #{} [field collection] (generic-get-percentages field collection))
(defmethod has-percentage #{} [field ref-field-map collection] (generic-has-percentage field ref-field-map collection))
(defmethod get-attributes #{} [field collection] (generic-get-attributes field collection))
(defmethod get-attributes-definition #{} [field ref-field-map collection mode] (generic-get-attributes-definition field ref-field-map collection mode))
(defmethod field-column-padding #{} [options field] (generic-field-column-padding options field))
(defmethod unpack #{} [field _] (generic-unpack field))

(defmethod get-value #{:collection} [field collection] (generic-get-value field collection))
(defmethod get-form-value #{:collection} [field value-atoms] (generic-get-form-value field value-atoms))
(defmethod get-objects #{:collection} [field collection] (generic-get-objects field collection))
(defmethod get-percentages #{:collection} [field collection] (generic-get-percentages field collection))
(defmethod has-percentage #{:collection} [field ref-field-map collection]
  (let [ownership-field (first 
                          (filter (fn [fld]
                                    (and
                                      (= "ownership" (:type fld))
                                      (= (:ref field) (:collection_ownership_field_ref fld))))
                            (vals ref-field-map)))]
    (when ownership-field
      (if-let [percentage-visibility (:percentage_visibility ownership-field)]
        (is-visible
          (assoc ownership-field
            :visibility percentage-visibility)
          (fn [aref]
            (get-in collection [:data (keyword aref)])))
        (not-empty (:unit_selector_field_ref ownership-field))))))

(defmethod get-attributes #{:collection} [field collection] (generic-get-attributes field collection))
(defmethod get-attributes-definition #{:collection} [field ref-field-map collection mode] (generic-get-attributes-definition field ref-field-map collection mode))
(defmethod field-column-padding #{:collection} [options field] (generic-field-column-padding options field))
(defmethod unpack #{:collection} [field _] (generic-unpack field))


(defn ownership-get [field collection suffix]
  (let [users-ref (:user_ownership_field_ref field)
        col-ref (:collection_ownership_field_ref field)]
    (into {} (seq (filter identity
                          (concat
                           (map (fn [[id value]]
                                  [[(keyword users-ref) id]
                                   (if (map? value)
                                     (assoc value
                                            :item-type :user)
                                     value) value])
                                (collection-data collection (str users-ref suffix)))
                           (map (fn [[id value]]
                                  [[(keyword col-ref) id]
                                   (if (map? value)
                                     (assoc value
                                            :item-type :collection)
                                     value)])
                                (collection-data collection (str col-ref suffix)))))))))

(defmethod get-value #{:ownership} [field collection]
  (let [users-ref (:user_ownership_field_ref field)
        col-ref (:collection_ownership_field_ref field)]
    (seq (filter identity
                 (concat
                  (map (fn [data] [(keyword users-ref) (-> data str keyword)])
                       (collection-data collection users-ref))
                  (map (fn [data] [(keyword col-ref) (-> data str keyword)])
                       (collection-data collection col-ref)))))))

(defmethod get-form-value #{:ownership} [field value-atoms]
  (let [users-ref (:user_ownership_field_ref field)
        col-ref (:collection_ownership_field_ref field)]
    (seq (filter identity
                 (concat
                  (map (fn [data] [(keyword users-ref) (-> data str keyword)])
                       (get-form-value {:ref users-ref} value-atoms))
                  (map (fn [data] [(keyword col-ref) (-> data str keyword)])
                       (get-form-value {:ref col-ref} value-atoms)))))))

(defmethod get-objects #{:ownership} [field collection]
  (ownership-get field collection "_objects"))

(defmethod get-percentages #{:ownership} [field collection]
  (ownership-get field collection "_percentage"))

(defmethod has-percentage #{:ownership} [_ _ _] false)

(defmethod get-attributes #{:ownership} [field collection]
  (ownership-get field collection "_attrs"))

(defmethod get-attributes-definition #{:ownership} [field ref-field-map collection mode]
  (generic-get-attributes-definition (ref-field-map (:collection_ownership_field_ref field)) ref-field-map collection mode))

(defmethod field-column-padding #{:ownership} [{:keys [form-fields]
                                                :as options} field]
  (let [field ((util/associate-by :ref form-fields) (:collection_ownership_field_ref field))]
    (-
     (:max-attribute-column-count options)
     (identity ((:attribute-column-counts options) (:ref field))))))

(defmethod unpack #{:ownership} [field ref-field-map]
  [(-> field :user_ownership_field_ref (ref-field-map))
   (-> field :collection_ownership_field_ref (ref-field-map))])

(defn has-value [field collection] (util/pytrue? (get-value field collection)))
(defn has-nested-value
  ([field collection has-value-fn]
   (or (has-value-fn field collection) (seq (filter (fn [subfield] (has-nested-value subfield (field-collection subfield collection) has-value-fn)) (:subfields field)))))
  ([field collection] (has-nested-value field collection has-value)))

(defn fields-with-cta-attrs [fields]
  (filter (fn [fld]
            (some :cta (:attributes fld))) fields))

(defn field-cta-attributes [field ref-field-map mode] (filter :cta (get-attributes-definition field ref-field-map mode)))

(defn unpack-fields [fields ref-field-map]
  (flatten (map #(unpack % ref-field-map) fields)))

(defn cta-highlight? [field value-fn to-value]
  (if (:cta field)
    (if-let [cta-config (:cta_config field)]
      (do
        (debug (str (:ref field) " CTA highlight condition string") (:highlight_condition cta-config))
        (spy :debug (str (:ref field) " CTA higlight condition evaluated")
             (sci/eval-string
              (:highlight_condition cta-config)
              {:namespaces {'user {'get-value value-fn
                                   'to-value to-value}}})))
      true)
    false))

(defn cta-warning? [field value-fn to-value]
  (if (:cta field)
    (if-let [cta-config (:cta_config field)]
      (do
        (debug (str (:ref field) " CTA warning condition string") (:warning_condition cta-config))
        (spy :debug (str (:ref field) " CTA warning condition evaluated")
             (sci/eval-string
              (:warning_condition cta-config)
              {:namespaces {'user {'get-value value-fn
                                   'to-value to-value}}})))
      true)
    false))

(defn other-choice-value [choice-field]
  (if-let [other-value (:other_choice_use_existing_choice choice-field)]
    other-value
    "<OTHER>"))

(defn is-other-choice-value [choice-field value]
  (= (other-choice-value choice-field) (str value)))

(defn filter-by-replicable-visibility [fields value-atoms]
  (let [visibility-refs (set (remove nil? (map :replicable_visibility_ref fields)))
        visibility-refs-max-count (into {}
                                        (map (fn [ref]
                                               [ref (js/parseInt (or @(value-atoms ref) "1"))]))
                                        visibility-refs)]
    (mapcat (fn [field]
              (if-let [ref (:replicable_visibility_ref field)]
                (let [index (count (filter #(= (:replicable_visibility_ref %) ref) (take-while #(not= % field) fields)))]
                  (when (< index (get visibility-refs-max-count ref))
                    [field]))
                [field]))
            fields)))

(defn get-refs [field-group]
  (let [refs (map :ref (:subfields field-group))]
    (concat refs (mapcat get-refs (filter :subfields (:subfields field-group))))))

(defn get-entity-fields [ref-field-map]
  (filter
    (fn [field]
      (= "entity" (:type field)))
    (flatten-fields (vals ref-field-map))))

(defn load-entities  [{:keys [get-values-for-fields-fn
                              collection-id-map-cb
                              collection-id-value-map-cb
                              collection-id-fields-cb]} ids]
  (controller/dispatch
    :collections
    :load
    {:ids ids
     :on-success (fn [results]
                   (let [collection-type-id-collection-map (into {} (group-by :collection_type_id results))
                         collection-type-ids (keys collection-type-id-collection-map)]
                     (some-> collection-id-map-cb (apply (util/associate-by :id results)))
                     (controller/dispatch
                       :collection-type-form-def
                       :load-many
                       {:collection-type-ids collection-type-ids
                        :on-success (fn [form-def-results]
                                      (let [collection-type-id-form-def-pairs (map vector collection-type-ids form-def-results)
                                            collection-ids-form-def-pairs (map
                                                                            (fn [[collection-type-id form-def]]
                                                                              [(map :id(collection-type-id-collection-map collection-type-id))
                                                                               form-def])
                                                                            collection-type-id-form-def-pairs)
                                            collection-id-fields (into {}
                                                                         (apply concat
                                                                           (map
                                                                             (fn [[collection-ids form-def]]
                                                                               (map
                                                                                 (fn [collection-id]
                                                                                   [collection-id (flatten-fields (apply concat (mapv :fields (:forms form-def))) false)])
                                                                                 collection-ids))
                                                                             collection-ids-form-def-pairs)))
                                            collection-id-value-atoms (into {}
                                                                        (map
                                                                          (fn [col]
                                                                            [(:id col)
                                                                             (get-values-for-fields-fn
                                                                               col
                                                                               (collection-id-fields (:id col))
                                                                               #_(flatten-fields (apply concat (mapv :fields (:forms (collection-id-form-def-map (:id col)))))))])
                                                                          results))]
                                        (collection-id-fields-cb (update-vals collection-id-fields (fn [vs]
                                                                                                     (util/associate-by :ref vs))) )
                                        (collection-id-value-map-cb collection-id-value-atoms)))})))}))

(defn has-entity-link-config [field] (not-empty (:entity_override_config field)))
(defn entity-field-ref [field] (get-in field [:entity_override_config :entity_field_ref]))
(defn entity-source-field-refs [field] (get-in field [:entity_override_config :entity_source_field_refs]))
(defn entity-attr-ref [field] (get-in field [:entity_override_config :entity_attr_ref]))

(defn get-ownership-info [entity-field-ref collection]
  (get-in collection [:data (keyword (str entity-field-ref "_ownership"))]))
